import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import {
  Users, UserPlus, Crown, Trash2, Save, Edit2, X, DollarSign, Percent
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TeamManagement() {
  const { user } = useAuth();
  const [team, setTeam] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addingMember, setAddingMember] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newSplit, setNewSplit] = useState(90);
  const [newOverride, setNewOverride] = useState(0);
  const [editingId, setEditingId] = useState(null);
  const [editSplit, setEditSplit] = useState(90);
  const [editOverride, setEditOverride] = useState(0);
  const [saving, setSaving] = useState(false);

  const fetchTeam = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/my-team`, { withCredentials: true });
      setTeam(res.data.team);
      setMembers(res.data.members || []);
    } catch { /* not a team leader */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchTeam(); }, [fetchTeam]);

  const handleAddMember = async () => {
    if (!newEmail.trim()) return;
    setSaving(true);
    try {
      await axios.post(`${API}/my-team/members`, {
        agent_email: newEmail.trim(), commission_split: newSplit, override_percentage: newOverride
      }, { withCredentials: true });
      toast.success('Member added to team');
      setNewEmail(''); setNewSplit(90); setNewOverride(0); setAddingMember(false);
      fetchTeam();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add member'); }
    finally { setSaving(false); }
  };

  const handleUpdateMember = async (memberId) => {
    setSaving(true);
    try {
      await axios.patch(`${API}/my-team/members/${memberId}`, {
        commission_split: editSplit, override_percentage: editOverride
      }, { withCredentials: true });
      toast.success('Member updated');
      setEditingId(null);
      fetchTeam();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
    finally { setSaving(false); }
  };

  const handleRemoveMember = async (memberId) => {
    try {
      await axios.delete(`${API}/my-team/members/${memberId}`, { withCredentials: true });
      toast.success('Member removed');
      fetchTeam();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to remove'); }
  };

  if (loading) return <div className="text-center py-12 text-slate-400">Loading...</div>;

  if (!team) {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="no-team">
        <h1 className="text-3xl font-['Cabinet_Grotesk'] font-bold mb-1 text-slate-900">Team Management</h1>
        <p className="text-sm text-slate-500 mb-8">You are not currently a team leader. Contact admin to set up a team.</p>
        <div className="bento-card p-12 rounded-xl text-center">
          <Users size={40} className="text-slate-200 mx-auto mb-4" />
          <p className="text-slate-400 text-sm">No team assigned</p>
        </div>
      </motion.div>
    );
  }

  const leader = members.find(m => m.role === 'leader');
  const agents = members.filter(m => m.role !== 'leader');

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="team-management">
      <h1 className="text-3xl font-['Cabinet_Grotesk'] font-bold mb-1 text-slate-900">Team Management</h1>
      <p className="text-sm text-slate-500 mb-8">Manage your team's commission splits and overrides.</p>

      {/* Visual Team Diagram */}
      <div className="bento-card p-6 rounded-xl mb-8" data-testid="team-diagram">
        <h3 className="text-sm font-semibold text-slate-800 mb-6 flex items-center gap-2">
          <Users size={16} className="text-[#002FA7]" /> {team.name}
        </h3>

        <div className="flex flex-col items-center">
          {/* Leader Node */}
          <div className="relative" data-testid="leader-node">
            <div className="bg-[#002FA7] text-white rounded-xl px-6 py-4 text-center min-w-[200px] shadow-lg shadow-[#002FA7]/20">
              <Crown size={18} className="mx-auto mb-1.5 text-amber-300" />
              <div className="text-sm font-semibold">{leader?.agent_email?.split('@')[0] || team.leader_email.split('@')[0]}</div>
              <div className="text-[10px] text-blue-200 mt-0.5">{team.leader_email}</div>
              <div className="flex items-center justify-center gap-3 mt-2">
                <span className="text-[10px] bg-white/20 px-2 py-0.5 rounded-full">
                  Split: {leader?.commission_split || 90}%
                </span>
                <span className="text-[9px] bg-amber-400/30 text-amber-100 px-2 py-0.5 rounded-full font-semibold">
                  LEADER
                </span>
              </div>
            </div>
          </div>

          {/* Connector Lines */}
          {agents.length > 0 && (
            <div className="flex flex-col items-center">
              <div className="w-px h-8 bg-slate-300" />
              <div className="relative w-full" style={{ maxWidth: `${Math.max(agents.length * 220, 220)}px` }}>
                {agents.length > 1 && (
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 h-px bg-slate-300" style={{ width: `${((agents.length - 1) / agents.length) * 100}%` }} />
                )}
              </div>
            </div>
          )}

          {/* Member Nodes */}
          {agents.length > 0 && (
            <div className="flex flex-wrap justify-center gap-5 mt-2" data-testid="member-nodes">
              {agents.map((member) => (
                <div key={member.id} className="flex flex-col items-center">
                  <div className="w-px h-4 bg-slate-300" />
                  <div className="bg-white border-2 border-slate-200 rounded-xl px-5 py-4 text-center min-w-[190px] hover:border-[#002FA7]/30 transition-colors" data-testid={`member-node-${member.id}`}>
                    <div className="w-9 h-9 bg-[#EFF3FF] rounded-full flex items-center justify-center mx-auto mb-2">
                      <span className="text-sm font-bold text-[#002FA7]">{member.agent_email?.[0]?.toUpperCase()}</span>
                    </div>
                    <div className="text-sm font-medium text-slate-800">{member.agent_email?.split('@')[0]}</div>
                    <div className="text-[10px] text-slate-400 mt-0.5">{member.agent_email}</div>
                    <div className="flex items-center justify-center gap-2 mt-2.5">
                      <span className="text-[10px] bg-[#EFF3FF] text-[#002FA7] px-2 py-0.5 rounded-full font-semibold">
                        {member.commission_split}% split
                      </span>
                      {member.override_percentage > 0 && (
                        <span className="text-[10px] bg-amber-50 text-amber-600 px-2 py-0.5 rounded-full font-semibold">
                          {member.override_percentage}% override
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {agents.length === 0 && (
            <div className="mt-6 text-center text-sm text-slate-400">No members yet. Add agents to your team below.</div>
          )}
        </div>
      </div>

      {/* Member Configuration Table */}
      <div className="bento-card rounded-xl overflow-hidden mb-6" data-testid="member-config">
        <div className="px-5 py-4 flex items-center justify-between border-b border-slate-100">
          <h3 className="text-sm font-semibold text-slate-800">Splits & Overrides</h3>
          <Button onClick={() => setAddingMember(!addingMember)} size="sm" className="bg-[#002FA7] text-white hover:bg-[#002075] rounded-lg text-xs" data-testid="add-member-btn">
            <UserPlus size={12} className="mr-1.5" /> Add Member
          </Button>
        </div>

        {/* Add Member Form */}
        {addingMember && (
          <div className="px-5 py-4 bg-slate-50 border-b border-slate-100" data-testid="add-member-form">
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
              <div className="sm:col-span-2">
                <Label className="text-[10px] text-slate-400 uppercase tracking-wider mb-1.5 block">Agent Email</Label>
                <Input value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="agent@example.com" className="bg-white border-slate-200 rounded-lg text-sm" data-testid="new-member-email" />
              </div>
              <div>
                <Label className="text-[10px] text-slate-400 uppercase tracking-wider mb-1.5 block">Commission Split %</Label>
                <Input type="number" min="0" max="100" value={newSplit} onChange={(e) => setNewSplit(parseFloat(e.target.value) || 0)} className="bg-white border-slate-200 rounded-lg text-sm" data-testid="new-member-split" />
              </div>
              <div>
                <Label className="text-[10px] text-slate-400 uppercase tracking-wider mb-1.5 block">Override %</Label>
                <Input type="number" min="0" max="100" value={newOverride} onChange={(e) => setNewOverride(parseFloat(e.target.value) || 0)} className="bg-white border-slate-200 rounded-lg text-sm" data-testid="new-member-override" />
              </div>
            </div>
            <div className="flex gap-2 mt-3">
              <Button onClick={handleAddMember} disabled={saving} size="sm" className="bg-[#002FA7] text-white hover:bg-[#002075] rounded-lg text-xs" data-testid="confirm-add-member">
                {saving ? 'Adding...' : 'Add to Team'}
              </Button>
              <Button variant="outline" size="sm" onClick={() => setAddingMember(false)} className="border-slate-200 text-slate-400 rounded-lg text-xs">Cancel</Button>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="grid grid-cols-12 gap-2 px-5 py-2.5 text-[9px] tracking-wider uppercase text-slate-400 font-semibold bg-slate-50/50 border-b border-slate-100">
          <div className="col-span-4">Agent</div>
          <div className="col-span-2">Role</div>
          <div className="col-span-2">Commission Split</div>
          <div className="col-span-2">Override</div>
          <div className="col-span-2">Actions</div>
        </div>

        {/* Leader Row */}
        {leader && (
          <div className="grid grid-cols-12 gap-2 px-5 py-3 items-center border-b border-slate-50 bg-[#002FA7]/[0.02]">
            <div className="col-span-4 text-xs font-medium text-slate-700 flex items-center gap-2">
              <Crown size={12} className="text-amber-500" /> {leader.agent_email}
            </div>
            <div className="col-span-2"><Badge className="text-[9px] bg-[#EFF3FF] text-[#002FA7] border-[#002FA7]/20">Leader</Badge></div>
            <div className="col-span-2 text-xs font-semibold text-[#002FA7]">{leader.commission_split}%</div>
            <div className="col-span-2 text-xs text-slate-400">—</div>
            <div className="col-span-2 text-[10px] text-slate-300">Team leader</div>
          </div>
        )}

        {/* Agent Rows */}
        {agents.map(member => (
          <div key={member.id} className="grid grid-cols-12 gap-2 px-5 py-3 items-center border-b border-slate-50" data-testid={`config-member-${member.id}`}>
            <div className="col-span-4 text-xs text-slate-600">{member.agent_email}</div>
            <div className="col-span-2"><Badge variant="outline" className="text-[9px] border-slate-200 text-slate-400">Member</Badge></div>
            <div className="col-span-2">
              {editingId === member.id ? (
                <Input type="number" min="0" max="100" value={editSplit} onChange={(e) => setEditSplit(parseFloat(e.target.value) || 0)} className="w-20 h-7 text-xs border-slate-200 rounded" data-testid={`edit-split-${member.id}`} />
              ) : (
                <span className="text-xs font-semibold text-[#002FA7]">{member.commission_split}%</span>
              )}
            </div>
            <div className="col-span-2">
              {editingId === member.id ? (
                <Input type="number" min="0" max="100" value={editOverride} onChange={(e) => setEditOverride(parseFloat(e.target.value) || 0)} className="w-20 h-7 text-xs border-slate-200 rounded" data-testid={`edit-override-${member.id}`} />
              ) : (
                <span className="text-xs text-slate-500">{member.override_percentage || 0}%</span>
              )}
            </div>
            <div className="col-span-2 flex items-center gap-1.5">
              {editingId === member.id ? (
                <>
                  <button onClick={() => handleUpdateMember(member.id)} className="text-emerald-600 hover:text-emerald-700" data-testid={`save-member-${member.id}`}><Save size={13} /></button>
                  <button onClick={() => setEditingId(null)} className="text-slate-300 hover:text-slate-500"><X size={13} /></button>
                </>
              ) : (
                <>
                  <button onClick={() => { setEditingId(member.id); setEditSplit(member.commission_split); setEditOverride(member.override_percentage || 0); }} className="text-slate-300 hover:text-[#002FA7]" data-testid={`edit-member-${member.id}`}><Edit2 size={13} /></button>
                  <button onClick={() => handleRemoveMember(member.id)} className="text-slate-300 hover:text-red-400" data-testid={`remove-member-${member.id}`}><Trash2 size={13} /></button>
                </>
              )}
            </div>
          </div>
        ))}

        {members.length === 0 && (
          <div className="p-8 text-center text-sm text-slate-400">No team members yet</div>
        )}
      </div>

      {/* Override Explanation */}
      <div className="bento-card p-5 rounded-xl">
        <h4 className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1.5"><Percent size={12} className="text-[#002FA7]" /> How Overrides Work</h4>
        <p className="text-xs text-slate-400 leading-relaxed">
          The <strong>commission split</strong> determines the agent's share of each transaction. The <strong>override percentage</strong> is an additional bonus paid to the team leader on the agent's deals. For example, if an agent has a 90% split with a 5% override, the agent keeps 85% and 5% goes to the team leader as an override.
        </p>
      </div>
    </motion.div>
  );
}
